import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import { FtpFilePicker } from '@/components/FtpFilePicker';
import { FtpFolderPicker } from '@/components/FtpFolderPicker';
import { api } from '@/services/api';
import { customToast } from '@/components/ui/custom-toast';
import {
  Loader2,
  Check,
  AlertCircle,
  Music,
  Server,
  FolderOutput,
  XCircle,
  Rss,
  Webhook,
  Plus,
  Trash2,
  Edit2,
  X,
  ImageIcon,
  Radio,
  Gauge,
  Mail,
  PlugZap,
  Newspaper,
  Mic2,
  Settings2,
  Key,
  FlaskConical,
  FileCheck,
  Link2,
  KeyRound,
  Clock,
  RotateCcw,
} from 'lucide-react';
import { PodbeanCredentialsForm } from '@/components/settings/PodbeanCredentialsForm';
import { ApiKeyManagement } from '@/components/settings/ApiKeyManagement';
import { CompactInput } from '@/components/CompactInput';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useUserCompanies } from '@/contexts/UserCompaniesContext';
import { getCompanyInitials, getCompanyAvatarColor } from '@/lib/utils';
import { CompanyPicker } from 'shell/CompanyPicker';
// @ts-ignore - Module Federation dynamic import
import { APIKeyService } from 'shell/APIKeyService';

interface ArtworkOverlaySettings {
  x: number;
  y: number;
  fontSize: number;
  fontFamily: 'MinionPro-Bold';
  color: string;
  textAnchor: 'start' | 'middle' | 'end';
}

interface PodcastAudioSettings {
  introPath: string;
  outroPath: string;
  commercial1Path: string;
  commercial2Path: string;
  commercial3Path: string;
  commercial4Path: string;
  artworkPath: string;
  generatedPath: string;
  generatedClipsPath: string;
  artworkOverlay?: ArtworkOverlaySettings;
  maxArticlesPerEpisode?: number;
}

interface SftpSettings {
  host: string;
  port: number;
  username: string;
  password: string;
  basePath: string;
}

interface RSSFeed {
  id: string;
  url: string;
  label: string;
  enabled: boolean;
  createdAt?: string;
  createdBy?: string;
}

interface WebhookSettings {
  url: string;
  apiKeyHeader: string;
}

interface PodbeanChannel {
  id: string;
  title: string;
  website?: string;
}

interface PodbeanSettings {
  clientId: string;
  clientSecret: string;
  channel?: PodbeanChannel | null;
}

interface RateLimitsSettings {
  anthropic: number;
  anthropicConcurrency: number;
  elevenlabs: number;
}

interface NotificationSettings {
  publishedEpisodeEmail: string;
}

interface AllSettings {
  podcastAudio: PodcastAudioSettings;
  sftp: SftpSettings;
  rssFeeds: RSSFeed[];
  webhooks: WebhookSettings;
  podbean: PodbeanSettings;
  rateLimits: RateLimitsSettings;
  notifications: NotificationSettings;
  publishTimePresets?: number[];
}

const defaultPodcastAudio: PodcastAudioSettings = {
  introPath: '',
  outroPath: '',
  commercial1Path: '',
  commercial2Path: '',
  commercial3Path: '',
  commercial4Path: '',
  artworkPath: '',
  generatedPath: '',
  generatedClipsPath: '',
  maxArticlesPerEpisode: 5,
};

const defaultSftp: SftpSettings = {
  host: '',
  port: 21,
  username: '',
  password: '',
  basePath: '',
};

const defaultWebhooks: WebhookSettings = {
  url: '',
  apiKeyHeader: '',
};

const defaultPodbean: PodbeanSettings = {
  clientId: '',
  clientSecret: '',
  channel: null,
};

const defaultRateLimits: RateLimitsSettings = {
  anthropic: 4,
  anthropicConcurrency: 1,
  elevenlabs: 10,
};

const defaultNotifications: NotificationSettings = {
  publishedEpisodeEmail: '',
};

const MAX_FEEDS = 6;

// Publish time presets constants
const DEFAULT_PUBLISH_TIME_PRESETS = [6, 7, 8, 9, 12];
const MAX_TIME_PRESETS = 6;
const AVAILABLE_HOURS = Array.from({ length: 24 }, (_, i) => i);

// Format hour (24hr) to display string (e.g., 6 -> "6:00 AM", 14 -> "2:00 PM")
const formatHourToDisplay = (hour: number): string => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:00 ${period}`;
};

type ConnectionStatus = 'untested' | 'testing' | 'success' | 'failed';

// Multi-email input component with badges
interface MultiEmailInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const MultiEmailInput: React.FC<MultiEmailInputProps> = ({ value, onChange, placeholder, disabled }) => {
  const [inputValue, setInputValue] = useState('');

  // Parse comma-separated string into array
  const emails = value
    .split(',')
    .map((e) => e.trim())
    .filter((e) => e.length > 0);

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const addEmail = (email: string) => {
    const trimmed = email.trim();
    if (!trimmed || !isValidEmail(trimmed)) return;
    if (emails.includes(trimmed)) return; // No duplicates

    const newEmails = [...emails, trimmed];
    onChange(newEmails.join(', '));
    setInputValue('');
  };

  const removeEmail = (emailToRemove: string) => {
    const newEmails = emails.filter((e) => e !== emailToRemove);
    onChange(newEmails.join(', '));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      addEmail(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      // Remove last email on backspace when input is empty
      removeEmail(emails[emails.length - 1]);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addEmail(inputValue);
    }
  };

  return (
    <div
      className={`flex flex-wrap gap-2 p-2 border rounded-md bg-background min-h-[42px] ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      {emails.map((email) => (
        <Badge key={email} variant="secondary" className="flex items-center gap-1 pr-1">
          {email}
          {!disabled && (
            <button
              type="button"
              onClick={() => removeEmail(email)}
              className="ml-1 hover:bg-muted rounded-full p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </Badge>
      ))}
      <input
        type="email"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={emails.length === 0 ? placeholder : ''}
        disabled={disabled}
        className="flex-1 min-w-[150px] bg-transparent outline-none text-sm"
      />
    </div>
  );
};

// Test configuration result types
interface TestResult {
  success: boolean;
  message: string;
  type?: string;
  statusCode?: number;
}

interface TestConfigurationResults {
  success: boolean;
  summary: { passed: number; failed: number; total: number };
  results: {
    ftpConnection: TestResult;
    paths: Record<string, TestResult>;
    webhook: {
      urlReachable: TestResult;
      apiKeyValid: TestResult;
      apiKeyBelongsToCompany: TestResult;
      headerConfigured: TestResult;
    };
    podbean?: {
      connection: TestResult;
      channelValid: TestResult;
    };
    rssFeeds?: {
      configured: TestResult;
      feedResults: Record<string, TestResult & { itemCount?: number }>;
    };
  };
}

export const PodcastSettings: React.FC = () => {
  // Multi-company state
  const { userCompanies, getCompanyColor } = useUserCompanies();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(() => {
    return localStorage.getItem('podcastManager_selectedCompanyId');
  });

  // Auto-select company for single-company users
  useEffect(() => {
    if (userCompanies.length === 1 && !selectedCompanyId) {
      const companyId = userCompanies[0].id;
      setSelectedCompanyId(companyId);
      localStorage.setItem('podcastManager_selectedCompanyId', companyId);
    }
  }, [userCompanies, selectedCompanyId]);

  // Tab state
  const [activeTab, setActiveTab] = useState<string>('connections');

  // Audio settings state
  const [podcastAudio, setPodcastAudio] = useState<PodcastAudioSettings>(defaultPodcastAudio);
  const [originalPodcastAudio, setOriginalPodcastAudio] = useState<PodcastAudioSettings>(defaultPodcastAudio);

  // SFTP settings state
  const [sftp, setSftp] = useState<SftpSettings>(defaultSftp);
  const [originalSftp, setOriginalSftp] = useState<SftpSettings>(defaultSftp);

  // RSS Feeds state
  const [rssFeeds, setRssFeeds] = useState<RSSFeed[]>([]);
  const [originalRssFeeds, setOriginalRssFeeds] = useState<RSSFeed[]>([]);
  const [editingFeedId, setEditingFeedId] = useState<string | null>(null);
  const [showAddFeedForm, setShowAddFeedForm] = useState(false);
  const [newFeedData, setNewFeedData] = useState({ url: '', label: '' });

  // Webhooks state
  const [webhooks, setWebhooks] = useState<WebhookSettings>(defaultWebhooks);
  const [originalWebhooks, setOriginalWebhooks] = useState<WebhookSettings>(defaultWebhooks);

  // PodBean state
  const [podbean, setPodbean] = useState<PodbeanSettings>(defaultPodbean);
  const [originalPodbean, setOriginalPodbean] = useState<PodbeanSettings>(defaultPodbean);
  const [podbeanSecretIsSet, setPodbeanSecretIsSet] = useState(false);

  // Rate Limits state
  const [rateLimits, setRateLimits] = useState<RateLimitsSettings>(defaultRateLimits);
  const [originalRateLimits, setOriginalRateLimits] = useState<RateLimitsSettings>(defaultRateLimits);

  // Notifications state
  const [notifications, setNotifications] = useState<NotificationSettings>(defaultNotifications);
  const [originalNotifications, setOriginalNotifications] = useState<NotificationSettings>(defaultNotifications);

  // Publish time presets state
  const [publishTimePresets, setPublishTimePresets] = useState<number[]>(DEFAULT_PUBLISH_TIME_PRESETS);
  const [originalPublishTimePresets, setOriginalPublishTimePresets] = useState<number[]>(DEFAULT_PUBLISH_TIME_PRESETS);

  // SFTP connection test state
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('untested');
  const [connectionMessage, setConnectionMessage] = useState('');

  // General state
  // Only show loading when we're actively fetching settings (not when waiting for company selection)
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Test configuration state
  const [configTestStatus, setConfigTestStatus] = useState<'idle' | 'testing' | 'complete'>('idle');
  const [configTestResults, setConfigTestResults] = useState<TestConfigurationResults | null>(null);
  const [showTestResults, setShowTestResults] = useState(false);

  // API Keys state for webhook info
  const [callbackApiKeyCount, setCallbackApiKeyCount] = useState<number>(0);

  // User role state for role-based visibility
  const [userRole, setUserRole] = useState<string>('user');
  const isSuperAdmin = userRole === 'super_admin';

  // Function to refresh callback key count - requires a selected company
  const refreshCallbackKeyCount = async () => {
    if (!selectedCompanyId) {
      setCallbackApiKeyCount(0);
      return;
    }

    try {
      const keys = await APIKeyService.getCompanyAPIKeys(selectedCompanyId);
      const callbackKeys = keys.filter(
        (k: { status: string; permissions: string[] }) =>
          k.status === 'active' && k.permissions.includes('podcast:callback')
      );
      setCallbackApiKeyCount(callbackKeys.length);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    }
  };

  // Fetch user role on mount for role-based visibility
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const response = await api.get<{ role: string }>('/users/me');
        if (response.success && response.data) {
          setUserRole(response.data.role);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };
    fetchUserRole();
  }, []);

  // Validate stored company on load - clear if user no longer has access
  useEffect(() => {
    if (userCompanies.length === 0) return;

    if (selectedCompanyId) {
      const isValid = userCompanies.some(c => c.id === selectedCompanyId);
      if (!isValid) {
        localStorage.removeItem('podcastManager_selectedCompanyId');
        setSelectedCompanyId(null);
      }
    }
  }, [selectedCompanyId, userCompanies]);

  // Refresh callback key count when switching to connections tab or when active company changes
  useEffect(() => {
    if (activeTab === 'connections') {
      refreshCallbackKeyCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, activeTab]);

  const loadSettings = async (companyIdOverride?: string) => {
    setLoading(true);
    setError(null);

    try {
      // Use the override if provided, otherwise use the selected company
      const targetCompanyId = companyIdOverride ?? selectedCompanyId;
      const url = targetCompanyId
        ? `/podcast/settings?companyId=${targetCompanyId}`
        : '/podcast/settings';
      const response = await api.get<AllSettings>(url);

      if (response.success && response.data) {
        // Load podcast audio settings
        const loadedAudio: PodcastAudioSettings = {
          introPath: response.data.podcastAudio?.introPath || '',
          outroPath: response.data.podcastAudio?.outroPath || '',
          commercial1Path: response.data.podcastAudio?.commercial1Path || '',
          commercial2Path: response.data.podcastAudio?.commercial2Path || '',
          commercial3Path: response.data.podcastAudio?.commercial3Path || '',
          commercial4Path: response.data.podcastAudio?.commercial4Path || '',
          artworkPath: response.data.podcastAudio?.artworkPath || '',
          generatedPath: response.data.podcastAudio?.generatedPath || '',
          generatedClipsPath: response.data.podcastAudio?.generatedClipsPath || '',
          artworkOverlay: response.data.podcastAudio?.artworkOverlay,
          maxArticlesPerEpisode: response.data.podcastAudio?.maxArticlesPerEpisode ?? 5,
        };
        setPodcastAudio(loadedAudio);
        setOriginalPodcastAudio(loadedAudio);

        // Load SFTP settings
        // Note: password comes back as '[ENCRYPTED]' from API for security - don't use that as actual password
        const storedPassword = response.data.sftp?.password || '';
        const hasStoredPassword = storedPassword === '[ENCRYPTED]';
        const loadedSftp = {
          host: response.data.sftp?.host || '',
          port: response.data.sftp?.port || 21,
          username: response.data.sftp?.username || '',
          password: '', // Never load password from API - user must re-enter to test/change
          basePath: response.data.sftp?.basePath || '',
        };
        setSftp(loadedSftp);
        setOriginalSftp(loadedSftp);

        // If SFTP is already configured (has host, username, and stored password), mark as success
        if (loadedSftp.host && loadedSftp.username && hasStoredPassword) {
          setConnectionStatus('success');
          setConnectionMessage('Previously configured (enter password to test again)');
        }

        // Load RSS feeds
        const loadedFeeds = response.data.rssFeeds || [];
        setRssFeeds(loadedFeeds);
        setOriginalRssFeeds(loadedFeeds);

        // Load webhooks
        const loadedWebhooks = {
          url: response.data.webhooks?.url || '',
          apiKeyHeader: response.data.webhooks?.apiKeyHeader || '',
        };
        setWebhooks(loadedWebhooks);
        setOriginalWebhooks(loadedWebhooks);

        // Load PodBean settings
        const loadedPodbean = {
          clientId: response.data.podbean?.clientId || '',
          clientSecret: '', // Never load actual secret, only indicator
          channel: response.data.podbean?.channel || null,
        };
        setPodbean(loadedPodbean);
        setOriginalPodbean(loadedPodbean);
        // Check if secret is set (API returns '[ENCRYPTED]' if set)
        setPodbeanSecretIsSet(response.data.podbean?.clientSecret === '[ENCRYPTED]');

        // Load Rate Limits
        const loadedRateLimits = {
          anthropic: response.data.rateLimits?.anthropic ?? defaultRateLimits.anthropic,
          anthropicConcurrency: response.data.rateLimits?.anthropicConcurrency ?? defaultRateLimits.anthropicConcurrency,
          elevenlabs: response.data.rateLimits?.elevenlabs ?? defaultRateLimits.elevenlabs,
        };
        setRateLimits(loadedRateLimits);
        setOriginalRateLimits(loadedRateLimits);

        // Load Notifications
        const loadedNotifications = {
          publishedEpisodeEmail: response.data.notifications?.publishedEpisodeEmail || '',
        };
        setNotifications(loadedNotifications);
        setOriginalNotifications(loadedNotifications);

        // Load Publish Time Presets (now included in main settings response)
        const loadedPresets = response.data.publishTimePresets?.length
          ? response.data.publishTimePresets
          : DEFAULT_PUBLISH_TIME_PRESETS;
        setPublishTimePresets(loadedPresets);
        setOriginalPublishTimePresets(loadedPresets);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Load settings when active company changes (initial load or company selection)
  useEffect(() => {
    if (selectedCompanyId) {
      loadSettings(selectedCompanyId);
      // Also refresh callback key count for new company
      refreshCallbackKeyCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const handleTestConnection = async () => {
    if (!sftp.host || !sftp.username || !sftp.password) {
      setError('Please fill in SFTP host, username, and password to test connection');
      return;
    }

    setConnectionStatus('testing');
    setConnectionMessage('');
    setError(null);

    try {
      const response = await api.post<{ success: boolean; message: string }>('/ftp/test-connection', {
        host: sftp.host,
        port: sftp.port || 21,
        username: sftp.username,
        password: sftp.password,
      });

      if (response.success && response.data?.success) {
        setConnectionStatus('success');
        setConnectionMessage(response.data.message || 'Connected successfully');
      } else {
        setConnectionStatus('failed');
        setConnectionMessage(response.data?.message || response.error?.message || 'Connection failed');
      }
    } catch (err) {
      console.error('FTP connection test error:', err);
      setConnectionStatus('failed');
      setConnectionMessage(err instanceof Error ? err.message : 'Connection test failed');
    }
  };

  /**
   * SECURITY: Validate RSS feed URL to prevent SSRF attacks
   * Blocks: private IPs, localhost, cloud metadata endpoints, non-HTTP protocols
   */
  const validateRssUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url);

      // Only allow HTTP and HTTPS protocols
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        console.warn('SECURITY: Blocked non-HTTP(S) protocol:', parsed.protocol);
        return false;
      }

      const hostname = parsed.hostname.toLowerCase();

      // Block localhost variants
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        console.warn('SECURITY: Blocked localhost access');
        return false;
      }

      // Block cloud metadata endpoint (AWS/GCP/Azure)
      if (hostname === '169.254.169.254') {
        console.warn('SECURITY: Blocked cloud metadata endpoint access');
        return false;
      }

      // Block private IP ranges (RFC 1918)
      const privateIpPatterns = [
        /^127\./,           // Loopback (127.0.0.0/8)
        /^10\./,            // Private class A (10.0.0.0/8)
        /^192\.168\./,      // Private class C (192.168.0.0/16)
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./, // Private class B (172.16.0.0/12)
        /^169\.254\./,      // Link-local (169.254.0.0/16)
        /^0\./,             // Reserved (0.0.0.0/8)
      ];

      for (const pattern of privateIpPatterns) {
        if (pattern.test(hostname)) {
          console.warn('SECURITY: Blocked private IP address:', hostname);
          return false;
        }
      }

      // Block raw IP addresses (require domain names for better traceability)
      if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) {
        console.warn('SECURITY: Blocked raw IP address. Please use a domain name.');
        return false;
      }

      // Require fully qualified domain name
      if (!hostname.includes('.')) {
        console.warn('SECURITY: Hostname must be a fully qualified domain name');
        return false;
      }

      return true;
    } catch {
      return false;
    }
  };

  const handleAddFeed = () => {
    if (!newFeedData.url.trim() || !newFeedData.label.trim()) {
      setError('URL and Label are required for RSS feeds');
      return;
    }

    if (!validateRssUrl(newFeedData.url)) {
      setError('Invalid RSS feed URL. Only public HTTP/HTTPS URLs with domain names are allowed.');
      return;
    }

    if (rssFeeds.length >= MAX_FEEDS) {
      setError(`Maximum ${MAX_FEEDS} RSS feeds allowed`);
      return;
    }

    const newFeed: RSSFeed = {
      id: `feed-${Date.now()}`,
      url: newFeedData.url.trim(),
      label: newFeedData.label.trim(),
      enabled: true,
      createdAt: new Date().toISOString(),
    };

    setRssFeeds([...rssFeeds, newFeed]);
    setNewFeedData({ url: '', label: '' });
    setShowAddFeedForm(false);
    setError(null);
  };

  const handleUpdateFeed = (feedId: string, updates: Partial<RSSFeed>) => {
    if (updates.url && !validateRssUrl(updates.url)) {
      setError('Invalid RSS feed URL. Only public HTTP/HTTPS URLs with domain names are allowed.');
      return;
    }

    setRssFeeds(rssFeeds.map(f => f.id === feedId ? { ...f, ...updates } : f));
    setEditingFeedId(null);
    setError(null);
  };

  const handleDeleteFeed = (feedId: string) => {
    if (!window.confirm('Are you sure you want to delete this RSS feed?')) {
      return;
    }
    setRssFeeds(rssFeeds.filter(f => f.id !== feedId));
  };

  const handleToggleFeed = (feedId: string) => {
    const feed = rssFeeds.find(f => f.id === feedId);
    if (feed?.enabled && !window.confirm(`Disable "${feed.label}"? Articles won't be fetched.`)) {
      return;
    }
    setRssFeeds(rssFeeds.map(f => f.id === feedId ? { ...f, enabled: !f.enabled } : f));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const targetCompanyId = selectedCompanyId;

      // Use different endpoint based on user role
      // Super admins use the full endpoint that can update all settings
      // Non-super admins use the /user endpoint that only updates allowed fields
      // This prevents non-super-admins from accidentally overwriting super_admin-only settings
      if (isSuperAdmin) {
        // Super admin: use full endpoint with all settings
        const podbeanPayload: { clientId: string; clientSecret?: string; channel?: PodbeanChannel | null } = {
          clientId: podbean.clientId,
          channel: podbean.channel,
        };
        if (podbean.clientSecret) {
          podbeanPayload.clientSecret = podbean.clientSecret;
        }

        const url = targetCompanyId
          ? `/podcast/settings?companyId=${targetCompanyId}`
          : '/podcast/settings';
        const response = await api.put(url, {
          podcastAudio,
          sftp,
          rssFeeds,
          webhooks,
          podbean: podbeanPayload,
          rateLimits,
          notifications,
          publishTimePresets,
        });

        if (response.success) {
          setOriginalPodcastAudio(podcastAudio);
          setOriginalSftp(sftp);
          setOriginalRssFeeds(rssFeeds);
          setOriginalWebhooks(webhooks);
          setOriginalPodbean({ ...podbean, clientSecret: '' });
          setOriginalRateLimits(rateLimits);
          setOriginalNotifications(notifications);
          setOriginalPublishTimePresets(publishTimePresets);
          // If we saved a new secret, mark it as set
          if (podbean.clientSecret) {
            setPodbeanSecretIsSet(true);
          }
          setPodbean({ ...podbean, clientSecret: '' });
          customToast.success('Settings saved successfully');
        } else {
          throw new Error(response.error?.message || 'Failed to save settings');
        }
      } else {
        // Non-super admin: use user endpoint with limited fields
        // This endpoint excludes: podbean, webhooks, rateLimits, generatedPath, generatedClipsPath
        const userPodcastAudio = {
          introPath: podcastAudio.introPath,
          outroPath: podcastAudio.outroPath,
          commercial1Path: podcastAudio.commercial1Path,
          commercial2Path: podcastAudio.commercial2Path,
          commercial3Path: podcastAudio.commercial3Path,
          commercial4Path: podcastAudio.commercial4Path,
          artworkPath: podcastAudio.artworkPath,
          artworkOverlay: podcastAudio.artworkOverlay,
          maxArticlesPerEpisode: podcastAudio.maxArticlesPerEpisode,
          // Note: generatedPath and generatedClipsPath are not sent - they're super_admin only
        };

        const url = targetCompanyId
          ? `/podcast/settings/user?companyId=${targetCompanyId}`
          : '/podcast/settings/user';
        const response = await api.put(url, {
          podcastAudio: userPodcastAudio,
          sftp,
          rssFeeds,
          notifications,
          publishTimePresets,
        });

        if (response.success) {
          setOriginalPodcastAudio(podcastAudio);
          setOriginalSftp(sftp);
          setOriginalRssFeeds(rssFeeds);
          setOriginalNotifications(notifications);
          setOriginalPublishTimePresets(publishTimePresets);
          customToast.success('Settings saved successfully');
        } else {
          throw new Error(response.error?.message || 'Failed to save settings');
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save settings';
      setError(message);
      customToast.error('Failed to save settings', { description: message });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPodcastAudio(originalPodcastAudio);
    setSftp(originalSftp);
    setRssFeeds(originalRssFeeds);
    setWebhooks(originalWebhooks);
    setPodbean(originalPodbean);
    setRateLimits(originalRateLimits);
    setNotifications(originalNotifications);
    setPublishTimePresets(originalPublishTimePresets);
    setConnectionStatus('untested');
    setEditingFeedId(null);
    setShowAddFeedForm(false);
    setNewFeedData({ url: '', label: '' });
    setError(null);
  };

  // Publish time presets handlers
  const handleAddTimePreset = (hour: number) => {
    if (publishTimePresets.length >= MAX_TIME_PRESETS) {
      customToast.error(`Maximum ${MAX_TIME_PRESETS} time presets allowed`);
      return;
    }
    if (publishTimePresets.includes(hour)) {
      customToast.error('This time is already in your presets');
      return;
    }
    setPublishTimePresets((prev) => [...prev, hour].sort((a, b) => a - b));
  };

  const handleRemoveTimePreset = (hour: number) => {
    if (publishTimePresets.length <= 1) {
      customToast.error('You must have at least one time preset');
      return;
    }
    setPublishTimePresets((prev) => prev.filter((h) => h !== hour));
  };

  const handleResetTimePresets = () => {
    setPublishTimePresets(DEFAULT_PUBLISH_TIME_PRESETS);
  };

  // Get available hours that aren't already selected
  const availableHoursToAdd = AVAILABLE_HOURS.filter((h) => !publishTimePresets.includes(h));

  const handleTestConfiguration = async () => {
    setConfigTestStatus('testing');
    setShowTestResults(true);
    setConfigTestResults(null);

    try {
      const targetCompanyId = selectedCompanyId;
      const url = targetCompanyId
        ? `/podcast/settings/test-configuration?companyId=${targetCompanyId}`
        : '/podcast/settings/test-configuration';
      const response = await api.post<TestConfigurationResults>(url, {});

      if (response.success && response.data) {
        setConfigTestResults(response.data);

        // Show toast based on results
        if (response.data.success) {
          customToast.success('All configuration tests passed');
        } else {
          customToast.error(`${response.data.summary.failed} of ${response.data.summary.total} tests failed`);
        }
      } else {
        throw new Error(response.error?.message || 'Test failed');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Configuration test failed';
      customToast.error('Test failed', { description: message });
      setConfigTestResults({
        success: false,
        summary: { passed: 0, failed: 1, total: 1 },
        results: {
          ftpConnection: { success: false, message },
          paths: {},
          webhook: {
            urlReachable: { success: false, message: 'Not tested' },
            apiKeyValid: { success: false, message: 'Not tested' },
            apiKeyBelongsToCompany: { success: false, message: 'Not tested' },
            headerConfigured: { success: false, message: 'Not tested' },
          },
          podbean: {
            connection: { success: false, message: 'Not tested' },
            channelValid: { success: false, message: 'Not tested' },
          },
        },
      });
    } finally {
      setConfigTestStatus('complete');
    }
  };

  const hasChanges =
    JSON.stringify(podcastAudio) !== JSON.stringify(originalPodcastAudio) ||
    JSON.stringify(sftp) !== JSON.stringify(originalSftp) ||
    JSON.stringify(rssFeeds) !== JSON.stringify(originalRssFeeds) ||
    JSON.stringify(webhooks) !== JSON.stringify(originalWebhooks) ||
    JSON.stringify(podbean) !== JSON.stringify(originalPodbean) ||
    JSON.stringify(rateLimits) !== JSON.stringify(originalRateLimits) ||
    JSON.stringify(notifications) !== JSON.stringify(originalNotifications) ||
    JSON.stringify(publishTimePresets) !== JSON.stringify(originalPublishTimePresets);

  const updateAudioSetting = (key: keyof PodcastAudioSettings) => (value: string) => {
    setPodcastAudio((prev) => ({ ...prev, [key]: value }));
  };

  const updateSftpSetting = <K extends keyof SftpSettings>(key: K, value: SftpSettings[K]) => {
    setSftp((prev) => ({ ...prev, [key]: value }));
    // Reset connection status when credentials change
    if (['host', 'port', 'username', 'password'].includes(key)) {
      setConnectionStatus('untested');
    }
  };

  const renderConnectionStatus = () => {
    switch (connectionStatus) {
      case 'testing':
        return (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Testing connection...</span>
          </div>
        );
      case 'success':
        return (
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
            <Check className="h-4 w-4" />
            <span>{connectionMessage || 'Connected successfully'}</span>
          </div>
        );
      case 'failed':
        return (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <XCircle className="h-4 w-4" />
            <span>{connectionMessage || 'Connection failed'}</span>
          </div>
        );
      default:
        return null;
    }
  };

  const FeedRow: React.FC<{ feed: RSSFeed }> = ({ feed }) => {
    const [editData, setEditData] = useState({ url: feed.url, label: feed.label });
    const isEditing = editingFeedId === feed.id;

    if (isEditing) {
      return (
        <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
          <div className="space-y-2">
            <Label>Feed Label</Label>
            <Input
              value={editData.label}
              onChange={(e) => setEditData({ ...editData, label: e.target.value })}
              placeholder="e.g., MDJ Online - Local News"
            />
          </div>
          <div className="space-y-2">
            <Label>Feed URL</Label>
            <Input
              value={editData.url}
              onChange={(e) => setEditData({ ...editData, url: e.target.value })}
              placeholder="https://example.com/rss"
            />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => handleUpdateFeed(feed.id, editData)}
              disabled={saving}
            >
              <Check className="h-4 w-4 mr-1" />
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditingFeedId(null)}
              disabled={saving}
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="border rounded-lg p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
        <div className="flex-1 min-w-0 w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-foreground">{feed.label}</h4>
            <Badge variant={feed.enabled ? 'default' : 'secondary'}>
              {feed.enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground break-all">{feed.url}</p>
        </div>
        <div className="flex items-center gap-2 ml-4 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleToggleFeed(feed.id)}
            disabled={saving}
          >
            {feed.enabled ? 'Disable' : 'Enable'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEditingFeedId(feed.id);
              setError(null);
            }}
            disabled={saving}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => handleDeleteFeed(feed.id)}
            disabled={saving}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  // Show loading spinner only when actively fetching settings
  if (loading) {
    return (
      <div className="max-w-content px-8 py-8">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // If companies haven't loaded yet (shell still fetching), show loading
  if (userCompanies.length === 0) {
    return (
      <div className="max-w-content px-8 py-8">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">Loading companies...</span>
        </div>
      </div>
    );
  }

  // If multi-company user hasn't selected a company yet, prompt them
  if (userCompanies.length > 1 && !selectedCompanyId) {
    return (
      <div className="max-w-content px-8 py-8">
        <div className="flex flex-col items-center justify-center py-16">
          <h1 className="text-h1 tracking-tight mb-4">Podcast Settings</h1>
          <p className="text-muted-foreground mb-4">Select a company to manage settings:</p>
          <CompanyPicker
            companies={userCompanies}
            selectedCompanyId={selectedCompanyId || undefined}
            onCompanyChange={(id) => {
              setSelectedCompanyId(id);
              localStorage.setItem('podcastManager_selectedCompanyId', id);
            }}
            getCompanyColor={getCompanyColor}
            placeholder="Select company..."
          />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Full Bleed Header Band */}
      <div className="bg-podcast border-b border-t border-border"
        style={{ padding: '20px 0 24px 0' }}
      >
        <div className="max-w-content" style={{ paddingLeft: '32px', paddingRight: '32px' }}>
          <div className="flex items-start justify-between">
            {/* Left side - Title and Company Picker */}
            <div>
              <h1
                className="text-h1"
                style={{ color: 'var(--pm-text-primary)' }}
              >
                Settings
              </h1>
              {/* Company selector for multi-company users - replaces subheading */}
              {userCompanies.length > 1 ? (
                <div className="mt-2">
                  <CompanyPicker
                    companies={userCompanies}
                    selectedCompanyId={selectedCompanyId || undefined}
                    onCompanyChange={(id) => {
                      setSelectedCompanyId(id);
                      localStorage.setItem('podcastManager_selectedCompanyId', id);
                    }}
                    getCompanyColor={getCompanyColor}
                    placeholder="Select company..."
                  />
                </div>
              ) : (
                <p className="mt-1" style={{ color: 'var(--pm-text-muted)' }}>
                  Configure connections, content sources, production settings, and more
                </p>
              )}
            </div>

            {/* Right side - Action Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleTestConfiguration}
                disabled={saving || configTestStatus === 'testing'}
              >
                {configTestStatus === 'testing' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <FlaskConical className="h-4 w-4 mr-2" />
                    Test Configuration
                  </>
                )}
              </Button>
              {hasChanges && (
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={saving}
                >
                  Cancel
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={saving || !hasChanges}
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-content px-8 py-8">
        {/* Test Configuration Results Dialog */}
      <Dialog open={showTestResults} onOpenChange={setShowTestResults}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" style={{ backgroundColor: 'var(--pm-bg-card)' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5" />
              Configuration Test Results
              {(() => {
                const testingCompanyId = selectedCompanyId;
                const testingCompany = userCompanies.find(c => c.id === testingCompanyId);
                if (testingCompany && userCompanies.length > 1) {
                  const avatarColor = getCompanyColor(testingCompany.id) || getCompanyAvatarColor(testingCompany.id);
                  return (
                    <Badge variant="outline" className="ml-2 font-normal">
                      <Avatar className="h-4 w-4 mr-1">
                        <AvatarFallback
                          className="text-[6px] font-medium text-white"
                          style={{ backgroundColor: avatarColor }}
                        >
                          {getCompanyInitials(testingCompany.name)}
                        </AvatarFallback>
                      </Avatar>
                      {testingCompany.name}
                    </Badge>
                  );
                }
                return null;
              })()}
            </DialogTitle>
            <DialogDescription>
              {configTestStatus === 'testing' ? (
                'Running configuration tests...'
              ) : configTestResults ? (
                <span className={configTestResults.success ? 'text-green-600' : 'text-red-600'}>
                  {configTestResults.summary.passed} of {configTestResults.summary.total} tests passed
                </span>
              ) : (
                'Test results will appear here'
              )}
            </DialogDescription>
          </DialogHeader>

          {configTestStatus === 'testing' && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {configTestResults && configTestStatus === 'complete' && (
            <div className="space-y-6">
              {/* FTP Connection */}
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <Server className="h-4 w-4" />
                  FTP Connection
                </h4>
                <div className="pl-6">
                  <div className={`flex items-center gap-2 ${configTestResults.results.ftpConnection.success ? 'text-green-600' : 'text-red-600'}`}>
                    {configTestResults.results.ftpConnection.success ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <span>{configTestResults.results.ftpConnection.message}</span>
                  </div>
                </div>
              </div>

              {/* FTP Paths */}
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <FileCheck className="h-4 w-4" />
                  FTP Paths
                </h4>
                <div className="pl-6 space-y-1">
                  {Object.entries(configTestResults.results.paths).map(([pathName, result]) => (
                    <div
                      key={pathName}
                      // MO_API-9 grey-to-red: render purely on success — drop the
                      // `type === 'skipped'` gray-out branch. "FTP connection
                      // required" rows are success:false underneath and now
                      // render red ❌ consistent with the FTP Connection ❌.
                      className={`flex items-center gap-2 text-sm ${
                        result.success ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {result.success ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <XCircle className="h-4 w-4" />
                      )}
                      <span className="font-mono text-xs">{pathName}:</span>
                      <span>{result.message}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Webhook */}
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <Webhook className="h-4 w-4" />
                  Webhook Configuration
                </h4>
                <div className="pl-6 space-y-1">
                  {/* URL Reachable */}
                  <div className={`flex items-center gap-2 text-sm ${
                    configTestResults.results.webhook.urlReachable.message.includes('optional')
                      ? 'text-muted-foreground'
                      : configTestResults.results.webhook.urlReachable.success
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {configTestResults.results.webhook.urlReachable.message.includes('optional') ? (
                      <span className="h-4 w-4 flex items-center justify-center">—</span>
                    ) : configTestResults.results.webhook.urlReachable.success ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <Link2 className="h-3 w-3" />
                    <span>URL: {configTestResults.results.webhook.urlReachable.message}</span>
                  </div>

                  {/* Header Configured */}
                  <div className={`flex items-center gap-2 text-sm ${
                    configTestResults.results.webhook.headerConfigured.message.includes('optional')
                      ? 'text-muted-foreground'
                      : configTestResults.results.webhook.headerConfigured.success
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {configTestResults.results.webhook.headerConfigured.message.includes('optional') ? (
                      <span className="h-4 w-4 flex items-center justify-center">—</span>
                    ) : configTestResults.results.webhook.headerConfigured.success ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <KeyRound className="h-3 w-3" />
                    <span>Header: {configTestResults.results.webhook.headerConfigured.message}</span>
                  </div>

                  {/* API Key Valid */}
                  <div className={`flex items-center gap-2 text-sm ${
                    configTestResults.results.webhook.apiKeyValid.message.includes('optional')
                      ? 'text-muted-foreground'
                      : configTestResults.results.webhook.apiKeyValid.success
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {configTestResults.results.webhook.apiKeyValid.message.includes('optional') ? (
                      <span className="h-4 w-4 flex items-center justify-center">—</span>
                    ) : configTestResults.results.webhook.apiKeyValid.success ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <Key className="h-3 w-3" />
                    <span>API Key: {configTestResults.results.webhook.apiKeyValid.message}</span>
                  </div>

                  {/* API Key Belongs to Company */}
                  <div className={`flex items-center gap-2 text-sm ${
                    configTestResults.results.webhook.apiKeyBelongsToCompany.message.includes('optional')
                      ? 'text-muted-foreground'
                      : configTestResults.results.webhook.apiKeyBelongsToCompany.success
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {configTestResults.results.webhook.apiKeyBelongsToCompany.message.includes('optional') ? (
                      <span className="h-4 w-4 flex items-center justify-center">—</span>
                    ) : configTestResults.results.webhook.apiKeyBelongsToCompany.success ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <Server className="h-3 w-3" />
                    <span>Company: {configTestResults.results.webhook.apiKeyBelongsToCompany.message}</span>
                  </div>
                </div>
              </div>

              {/* Podbean Section */}
              {configTestResults.results.podbean && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Radio className="h-4 w-4" />
                  Podbean
                </h4>
                <div className="space-y-1 ml-6">
                  {/* Podbean Connection */}
                  {/* MO_API-9 grey-to-red: render purely on success. Backend
                      now returns success:false for the "Podbean not configured
                      (optional)" case so this row renders red ❌. The optional
                      wording stays in the message text so the user knows it's
                      not a required failure. */}
                  <div className={`flex items-center gap-2 text-sm ${
                    configTestResults.results.podbean.connection.success
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {configTestResults.results.podbean.connection.success ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <Server className="h-3 w-3" />
                    <span>Connection: {configTestResults.results.podbean.connection.message}</span>
                  </div>

                  {/* Podbean Channel */}
                  <div className={`flex items-center gap-2 text-sm ${
                    configTestResults.results.podbean.channelValid.success
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {configTestResults.results.podbean.channelValid.success ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <Link2 className="h-3 w-3" />
                    <span>Channel: {configTestResults.results.podbean.channelValid.message}</span>
                  </div>
                </div>
              </div>
              )}

              {/* RSS Feeds Section */}
              {configTestResults.results.rssFeeds && (
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <Rss className="h-4 w-4" />
                  RSS Feeds
                </h4>
                <div className="pl-6 space-y-1">
                  {/* Configured status */}
                  <div className={`flex items-center gap-2 text-sm ${
                    configTestResults.results.rssFeeds.configured.message.includes('optional')
                      ? 'text-muted-foreground'
                      : configTestResults.results.rssFeeds.configured.success
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {configTestResults.results.rssFeeds.configured.message.includes('optional') ? (
                      <span className="h-4 w-4 flex items-center justify-center">—</span>
                    ) : configTestResults.results.rssFeeds.configured.success ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <span>{configTestResults.results.rssFeeds.configured.message}</span>
                  </div>

                  {/* Individual feed results */}
                  {Object.entries(configTestResults.results.rssFeeds.feedResults).map(([feedLabel, result]) => (
                    <div
                      key={feedLabel}
                      className={`flex items-center gap-2 text-sm ${
                        result.success ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {result.success ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <XCircle className="h-4 w-4" />
                      )}
                      <span className="font-medium">{feedLabel}:</span>
                      <span>
                        {result.message}
                        {result.itemCount !== undefined && result.success && (
                          <span className="text-muted-foreground ml-1">
                            ({result.itemCount} item{result.itemCount !== 1 ? 's' : ''})
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Tabbed Settings */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="connections" className="flex items-center gap-2">
            <PlugZap className="h-4 w-4" />
            Connections
          </TabsTrigger>
          <TabsTrigger value="content" className="flex items-center gap-2">
            <Newspaper className="h-4 w-4" />
            Content
          </TabsTrigger>
          <TabsTrigger value="production" className="flex items-center gap-2">
            <Mic2 className="h-4 w-4" />
            Production
          </TabsTrigger>
          <TabsTrigger value="advanced" className="flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            Advanced
          </TabsTrigger>
        </TabsList>

        {/* CONNECTIONS TAB */}
        <TabsContent value="connections" className="space-y-6">
          <Card style={{ backgroundColor: 'var(--pm-bg-card)' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PlugZap className="h-5 w-5" />
                External Connections
              </CardTitle>
              <CardDescription>
                Configure FTP storage, PodBean publishing, and webhook integrations.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible defaultValue="ftp" className="w-full">
                {/* FTP Connection Section */}
                <AccordionItem value="ftp">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Server className="h-4 w-4" />
                      <span>FTP Connection</span>
                      {connectionStatus === 'success' && (
                        <Badge variant="outline" className="ml-2 text-green-600 border-green-600">
                          <Check className="h-3 w-3 mr-1" />
                          Connected
                        </Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2">
                    <div className="space-y-0.5">
                      <CompactInput
                        label="Host"
                        value={sftp.host}
                        onChange={(value) => updateSftpSetting('host', value)}
                        placeholder="ftp.example.com"
                        disabled={saving}
                      />
                      <CompactInput
                        label="Port"
                        value={sftp.port}
                        onChange={(value) => updateSftpSetting('port', parseInt(value, 10) || 21)}
                        type="number"
                        placeholder="21"
                        disabled={saving}
                        min={1}
                        max={65535}
                      />
                      <CompactInput
                        label="Username"
                        value={sftp.username}
                        onChange={(value) => updateSftpSetting('username', value)}
                        placeholder="username"
                        disabled={saving}
                      />
                      <CompactInput
                        label="Password"
                        value={sftp.password}
                        onChange={(value) => updateSftpSetting('password', value)}
                        type="password"
                        placeholder="Enter password..."
                        disabled={saving}
                      />
                      <FtpFolderPicker
                        compact
                        label="Base Path"
                        value={sftp.basePath}
                        onChange={(value) => updateSftpSetting('basePath', value)}
                        disabled={saving || connectionStatus !== 'success'}
                        companyId={selectedCompanyId || undefined}
                      />
                    </div>
                    {/* Test Connection */}
                    <div className="flex items-center gap-3 pt-3 ml-5">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleTestConnection}
                        disabled={saving || connectionStatus === 'testing' || !sftp.host || !sftp.username || !sftp.password}
                      >
                        {connectionStatus === 'testing' ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Testing...
                          </>
                        ) : (
                          'Test Connection'
                        )}
                      </Button>
                      {renderConnectionStatus()}
                    </div>
                    {connectionStatus !== 'success' && (
                      <p className="text-sm text-muted-foreground ml-5 pt-1">
                        Test connection to enable folder browsing.
                      </p>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* PodBean Section - Super Admin Only */}
                {isSuperAdmin && (
                  <AccordionItem value="podbean">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Radio className="h-4 w-4" />
                        <span>PodBean Integration</span>
                        {podbean.clientId && podbeanSecretIsSet && (
                          <Badge variant="outline" className="ml-2 text-green-600 border-green-600">
                            <Check className="h-3 w-3 mr-1" />
                            Configured
                          </Badge>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2">
                      <PodbeanCredentialsForm
                        clientId={podbean.clientId}
                        secretIsSet={podbeanSecretIsSet}
                        channel={podbean.channel}
                        onChange={(clientId, clientSecret) => {
                          setPodbean({ ...podbean, clientId, clientSecret });
                        }}
                        onChannelChange={(channel) => {
                          setPodbean({ ...podbean, channel });
                        }}
                        disabled={saving}
                        companyId={selectedCompanyId || undefined}
                      />
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Webhooks Section - Super Admin Only */}
                {isSuperAdmin && (
                  <AccordionItem value="webhooks">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Webhook className="h-4 w-4" />
                        <span>Webhooks</span>
                        {webhooks.url && (
                          <Badge variant="outline" className="ml-2 text-green-600 border-green-600">
                            <Check className="h-3 w-3 mr-1" />
                            Configured
                          </Badge>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2">
                      <div className="space-y-0.5">
                        <CompactInput
                          label="Webhook URL"
                          value={webhooks.url}
                          onChange={(value) => setWebhooks({ ...webhooks, url: value })}
                          placeholder="https://hook.us1.make.com/..."
                          disabled={saving}
                        />
                        <CompactInput
                          label="Header Name"
                          value={webhooks.apiKeyHeader}
                          onChange={(value) => setWebhooks({ ...webhooks, apiKeyHeader: value })}
                          placeholder="X-API-Key"
                          disabled={saving}
                        />
                      </div>
                      <div className="rounded-lg border p-4 bg-muted/50 mt-4">
                        <div className="flex items-start gap-3">
                          <Key className="h-5 w-5 text-muted-foreground mt-0.5" />
                          <div className="flex-1">
                            <p className="font-medium text-sm">Webhook Authentication</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Callbacks use API keys from the API Keys section below.
                            </p>
                            {callbackApiKeyCount > 0 ? (
                              <p className="text-sm text-green-600 dark:text-green-400 mt-2 flex items-center gap-1">
                                <Check className="h-4 w-4" />
                                {callbackApiKeyCount} active key{callbackApiKeyCount !== 1 ? 's' : ''} with callback permission
                              </p>
                            ) : (
                              <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                                <AlertCircle className="h-4 w-4" />
                                No API keys with callback permission
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* API Keys Section - Super Admin Only */}
                {isSuperAdmin && (
                  <AccordionItem value="api-keys" className="border-b-0">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        <span>API Keys</span>
                        {callbackApiKeyCount > 0 && (
                          <Badge variant="outline" className="ml-2 text-green-600 border-green-600">
                            <Check className="h-3 w-3 mr-1" />
                            {callbackApiKeyCount} Active
                          </Badge>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2">
                      <ApiKeyManagement companyId={selectedCompanyId} />
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONTENT TAB */}
        <TabsContent value="content" className="space-y-6">
          {/* RSS Feeds Card */}
          <Card style={{ backgroundColor: 'var(--pm-bg-card)' }}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Rss className="h-5 w-5" />
                    RSS Feeds
                  </CardTitle>
                  <CardDescription>
                    Configure RSS feeds for the Podcast Manager to fetch articles
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    setShowAddFeedForm(true);
                    setError(null);
                  }}
                  disabled={rssFeeds.length >= MAX_FEEDS || showAddFeedForm || saving}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Feed
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {showAddFeedForm && (
                <div className="border-2 border-primary rounded-lg p-4 space-y-3 bg-primary/5">
                  <h4 className="font-medium text-foreground">Add New RSS Feed</h4>
                  <div className="space-y-2">
                    <Label>Feed Label *</Label>
                    <Input
                      value={newFeedData.label}
                      onChange={(e) => setNewFeedData({ ...newFeedData, label: e.target.value })}
                      placeholder="e.g., MDJ Online - Local News"
                      disabled={saving}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Feed URL *</Label>
                    <Input
                      value={newFeedData.url}
                      onChange={(e) => setNewFeedData({ ...newFeedData, url: e.target.value })}
                      placeholder="https://example.com/rss"
                      disabled={saving}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAddFeed} disabled={saving}>
                      <Check className="h-4 w-4 mr-1" />
                      Add Feed
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAddFeedForm(false);
                        setNewFeedData({ url: '', label: '' });
                        setError(null);
                      }}
                      disabled={saving}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {rssFeeds.length === 0 && !showAddFeedForm && (
                <div className="text-center py-8 text-muted-foreground">
                  <Rss className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p className="text-lg mb-1">No RSS feeds configured</p>
                  <p className="text-sm">Click "Add Feed" to get started</p>
                </div>
              )}
              {rssFeeds.length > 0 && (
                <div className="space-y-3">
                  {rssFeeds.map((feed) => (
                    <FeedRow key={feed.id} feed={feed} />
                  ))}
                </div>
              )}
              {rssFeeds.length > 0 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    {rssFeeds.length} of {MAX_FEEDS} feeds configured
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {rssFeeds.filter(f => f.enabled).length} enabled
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Episode Limits Card */}
          <Card style={{ backgroundColor: 'var(--pm-bg-card)' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Newspaper className="h-5 w-5" />
                Episode Limits
              </CardTitle>
              <CardDescription>
                Configure content limits for episodes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="maxArticles">Max Articles Per Episode</Label>
                <Select
                  value={String(podcastAudio.maxArticlesPerEpisode || 5)}
                  onValueChange={(value) => setPodcastAudio(prev => ({ ...prev, maxArticlesPerEpisode: parseInt(value) }))}
                  disabled={saving}
                >
                  <SelectTrigger id="maxArticles" className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                      <SelectItem key={num} value={String(num)}>
                        {num}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Maximum number of articles that can be added to a single episode.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PRODUCTION TAB */}
        <TabsContent value="production" className="space-y-4">
          {/* Audio Files Section */}
          {(() => {
            const audioConfigured = [
              podcastAudio.introPath,
              podcastAudio.outroPath,
              podcastAudio.commercial1Path,
              podcastAudio.commercial2Path,
              podcastAudio.commercial3Path,
              podcastAudio.commercial4Path,
            ].filter(Boolean).length;
            return (
              <Card style={{ backgroundColor: 'var(--pm-bg-card)' }}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Music className="h-4 w-4" />
                      Audio Files
                    </CardTitle>
                    <Badge variant={audioConfigured === 6 ? 'default' : 'secondary'}>
                      {audioConfigured} of 6 set
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-0.5">
                    <FtpFilePicker
                      compact
                      label="Intro"
                      value={podcastAudio.introPath}
                      onChange={updateAudioSetting('introPath')}
                      fileFilter=".mp3"
                      disabled={saving}
                      basePath={sftp.basePath}
                      companyId={selectedCompanyId || undefined}
                    />
                    <FtpFilePicker
                      compact
                      label="Outro"
                      value={podcastAudio.outroPath}
                      onChange={updateAudioSetting('outroPath')}
                      fileFilter=".mp3"
                      disabled={saving}
                      basePath={sftp.basePath}
                      companyId={selectedCompanyId || undefined}
                    />
                    <FtpFilePicker
                      compact
                      label="Commercial 1"
                      value={podcastAudio.commercial1Path}
                      onChange={updateAudioSetting('commercial1Path')}
                      fileFilter=".mp3"
                      disabled={saving}
                      basePath={sftp.basePath}
                      companyId={selectedCompanyId || undefined}
                    />
                    <FtpFilePicker
                      compact
                      label="Commercial 2"
                      value={podcastAudio.commercial2Path}
                      onChange={updateAudioSetting('commercial2Path')}
                      fileFilter=".mp3"
                      disabled={saving}
                      basePath={sftp.basePath}
                      companyId={selectedCompanyId || undefined}
                    />
                    <FtpFilePicker
                      compact
                      label="Commercial 3"
                      value={podcastAudio.commercial3Path}
                      onChange={updateAudioSetting('commercial3Path')}
                      fileFilter=".mp3"
                      disabled={saving}
                      basePath={sftp.basePath}
                      companyId={selectedCompanyId || undefined}
                    />
                    <FtpFilePicker
                      compact
                      label="Commercial 4"
                      value={podcastAudio.commercial4Path}
                      onChange={updateAudioSetting('commercial4Path')}
                      fileFilter=".mp3"
                      disabled={saving}
                      basePath={sftp.basePath}
                      companyId={selectedCompanyId || undefined}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Artwork Section */}
          {(() => {
            const artworkConfigured = podcastAudio.artworkPath ? 1 : 0;
            return (
              <Card style={{ backgroundColor: 'var(--pm-bg-card)' }}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <ImageIcon className="h-4 w-4" />
                      Artwork
                    </CardTitle>
                    <Badge variant={artworkConfigured === 1 ? 'default' : 'secondary'}>
                      {artworkConfigured} of 1 set
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  <FtpFilePicker
                    compact
                    label="Default"
                    value={podcastAudio.artworkPath}
                    onChange={updateAudioSetting('artworkPath')}
                    fileFilter=".jpg,.jpeg,.png,.gif,.webp"
                    disabled={saving}
                    basePath={sftp.basePath}
                    companyId={selectedCompanyId || undefined}
                  />
                  {/* Date Overlay Toggle */}
                  <div className="flex items-center justify-between py-2 px-1">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${podcastAudio.artworkOverlay ? 'bg-green-500' : 'bg-muted-foreground/30'}`} />
                      <span className="text-sm font-medium">Date Overlay</span>
                      <span className="text-sm text-muted-foreground">
                        {podcastAudio.artworkOverlay ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <Switch
                      checked={!!podcastAudio.artworkOverlay}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setPodcastAudio(prev => ({
                            ...prev,
                            artworkOverlay: {
                              x: 236,
                              y: 1755,
                              fontSize: 144,
                              fontFamily: 'MinionPro-Bold',
                              color: '#880E35',
                              textAnchor: 'middle',
                            },
                          }));
                        } else {
                          setPodcastAudio(prev => {
                            const { artworkOverlay, ...rest } = prev;
                            return rest as PodcastAudioSettings;
                          });
                        }
                      }}
                      disabled={saving}
                    />
                  </div>
                  {/* Overlay Settings - collapsed until enabled */}
                  {podcastAudio.artworkOverlay && (
                    <div className="ml-5 pl-4 border-l-2 border-muted space-y-3 py-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="overlayY" className="text-xs">Y Position</Label>
                          <Input
                            id="overlayY"
                            type="number"
                            value={podcastAudio.artworkOverlay.y}
                            onChange={(e) => setPodcastAudio(prev => ({
                              ...prev,
                              artworkOverlay: { ...prev.artworkOverlay!, y: parseInt(e.target.value, 10) || 0 },
                            }))}
                            disabled={saving}
                            min={0}
                            className="h-8"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="overlayFontSize" className="text-xs">Font Size</Label>
                          <Input
                            id="overlayFontSize"
                            type="number"
                            value={podcastAudio.artworkOverlay.fontSize}
                            onChange={(e) => setPodcastAudio(prev => ({
                              ...prev,
                              artworkOverlay: { ...prev.artworkOverlay!, fontSize: parseInt(e.target.value, 10) || 100 },
                            }))}
                            disabled={saving}
                            min={10}
                            max={500}
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="overlayColor" className="text-xs">Color</Label>
                          <div className="flex gap-2">
                            <Input
                              id="overlayColor"
                              type="text"
                              value={podcastAudio.artworkOverlay.color}
                              onChange={(e) => setPodcastAudio(prev => ({
                                ...prev,
                                artworkOverlay: { ...prev.artworkOverlay!, color: e.target.value },
                              }))}
                              disabled={saving}
                              placeholder="#880E35"
                              className="flex-1 h-8"
                            />
                            <input
                              type="color"
                              value={podcastAudio.artworkOverlay.color}
                              onChange={(e) => setPodcastAudio(prev => ({
                                ...prev,
                                artworkOverlay: { ...prev.artworkOverlay!, color: e.target.value },
                              }))}
                              disabled={saving}
                              className="w-8 h-8 rounded border cursor-pointer"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="overlayTextAnchor" className="text-xs">Alignment</Label>
                          <Select
                            value={podcastAudio.artworkOverlay.textAnchor}
                            onValueChange={(value: 'start' | 'middle' | 'end') => setPodcastAudio(prev => ({
                              ...prev,
                              artworkOverlay: { ...prev.artworkOverlay!, textAnchor: value },
                            }))}
                            disabled={saving}
                          >
                            <SelectTrigger id="overlayTextAnchor" className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="start">Left</SelectItem>
                              <SelectItem value="middle">Center</SelectItem>
                              <SelectItem value="end">Right</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {podcastAudio.artworkOverlay.textAnchor !== 'middle' && (
                        <div className="space-y-1">
                          <Label htmlFor="overlayX" className="text-xs">X Position</Label>
                          <Input
                            id="overlayX"
                            type="number"
                            value={podcastAudio.artworkOverlay.x}
                            onChange={(e) => setPodcastAudio(prev => ({
                              ...prev,
                              artworkOverlay: { ...prev.artworkOverlay!, x: parseInt(e.target.value, 10) || 0 },
                            }))}
                            disabled={saving}
                            min={0}
                            className="h-8 w-32"
                          />
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Format: "December 11, 2025"
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Output Folders Section - Super Admin Only */}
          {isSuperAdmin && (() => {
            const outputConfigured = podcastAudio.generatedPath ? 1 : 0;
            return (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <FolderOutput className="h-4 w-4" />
                      Output Folders
                    </CardTitle>
                    <Badge variant={outputConfigured === 1 ? 'default' : 'secondary'}>
                      {outputConfigured} of 1 set
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-0.5">
                    <FtpFolderPicker
                      compact
                      label="Episodes"
                      value={podcastAudio.generatedPath}
                      onChange={updateAudioSetting('generatedPath')}
                      disabled={saving || connectionStatus !== 'success'}
                      basePath={sftp.basePath}
                      companyId={selectedCompanyId || undefined}
                    />
                  </div>
                  {connectionStatus !== 'success' && (
                    <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Configure FTP connection in Connections tab first
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })()}
        </TabsContent>

        {/* ADVANCED TAB */}
        <TabsContent value="advanced" className="space-y-6">
          <Card style={{ backgroundColor: 'var(--pm-bg-card)' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gauge className="h-5 w-5" />
                Advanced Settings
              </CardTitle>
              <CardDescription>
                Rate limits, notifications, and system configuration.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible defaultValue={isSuperAdmin ? "rate-limits" : "notifications"} className="w-full">
                {/* Rate Limits Section - Super Admin Only */}
                {isSuperAdmin && (
                  <AccordionItem value="rate-limits">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Gauge className="h-4 w-4" />
                        <span>API Rate Limits</span>
                        <Badge variant="outline" className="ml-2">
                          {rateLimits.anthropic}/min
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4 pt-2">
                      <p className="text-sm text-muted-foreground">
                        Configure API request limits. Lower values prevent errors but slow processing.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="anthropicRateLimit">Anthropic Rate</Label>
                          <Select
                            value={String(rateLimits.anthropic)}
                            onValueChange={(value) => setRateLimits({ ...rateLimits, anthropic: parseInt(value) })}
                            disabled={saving}
                          >
                            <SelectTrigger id="anthropicRateLimit">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 4, 5, 6, 8, 10, 15, 20, 30, 60].map((num) => (
                                <SelectItem key={num} value={String(num)}>
                                  {num}/min
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">Script generation</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="anthropicConcurrency">Parallel Jobs</Label>
                          <Select
                            value={String(rateLimits.anthropicConcurrency)}
                            onValueChange={(value) => setRateLimits({ ...rateLimits, anthropicConcurrency: parseInt(value) })}
                            disabled={saving}
                          >
                            <SelectTrigger id="anthropicConcurrency">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                <SelectItem key={num} value={String(num)}>
                                  {num} job{num > 1 ? 's' : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">Concurrent scripts</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="elevenlabsRateLimit">ElevenLabs Rate</Label>
                          <Select
                            value={String(rateLimits.elevenlabs)}
                            onValueChange={(value) => setRateLimits({ ...rateLimits, elevenlabs: parseInt(value) })}
                            disabled={saving}
                          >
                            <SelectTrigger id="elevenlabsRateLimit">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 4, 5, 6, 8, 10, 15, 20, 30, 60].map((num) => (
                                <SelectItem key={num} value={String(num)}>
                                  {num}/min
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">Audio synthesis</p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Notifications Section */}
                <AccordionItem value="notifications">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      <span>Email Notifications</span>
                      {notifications.publishedEpisodeEmail && (
                        <Badge variant="outline" className="ml-2 text-green-600 border-green-600">
                          <Check className="h-3 w-3 mr-1" />
                          Configured
                        </Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <div className="space-y-2">
                      <Label>Published Episode Notification</Label>
                      <MultiEmailInput
                        value={notifications.publishedEpisodeEmail}
                        onChange={(value) => setNotifications({ ...notifications, publishedEpisodeEmail: value })}
                        placeholder="Add email address..."
                        disabled={saving}
                      />
                      <p className="text-sm text-muted-foreground">
                        Emails to notify when episodes publish. Includes player, artwork, and clip titles.
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Publish Time Presets Section */}
                <AccordionItem value="publish-times" className="border-b-0">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>Publish Time Presets</span>
                      <Badge variant="outline" className="ml-2">
                        {publishTimePresets.length} of {MAX_TIME_PRESETS}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <p className="text-sm text-muted-foreground">
                      Configure the quick-select time options shown when scheduling podcast episodes.
                    </p>

                    {/* Current Presets */}
                    <div>
                      <Label className="mb-2 block">Current Presets</Label>
                      <div className="flex flex-wrap gap-2">
                        {publishTimePresets.map((hour) => (
                          <div
                            key={hour}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-lg group"
                          >
                            <span className="text-sm font-medium">{formatHourToDisplay(hour)}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveTimePreset(hour)}
                              className="text-muted-foreground hover:text-destructive transition-colors"
                              title="Remove this preset"
                              disabled={saving}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Add New Preset */}
                    {publishTimePresets.length < MAX_TIME_PRESETS && (
                      <div>
                        <Label className="mb-2 block">Add Preset</Label>
                        <div className="flex items-center gap-2">
                          <Select onValueChange={(value) => handleAddTimePreset(parseInt(value, 10))}>
                            <SelectTrigger className="w-[140px]">
                              <SelectValue placeholder="Select time..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableHoursToAdd.map((hour) => (
                                <SelectItem key={hour} value={hour.toString()}>
                                  {formatHourToDisplay(hour)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="text-xs text-muted-foreground">
                            {MAX_TIME_PRESETS - publishTimePresets.length} slot
                            {MAX_TIME_PRESETS - publishTimePresets.length !== 1 ? 's' : ''} remaining
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Reset to Defaults */}
                    <div className="pt-2 border-t">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleResetTimePresets}
                        disabled={saving}
                        className="flex items-center gap-1.5"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Reset to Defaults
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>
    </>
  );
};

export default PodcastSettings;
